import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  Typography, 
  CircularProgress, 
  Alert, 
  Box,
  IconButton,
  Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { buildApiUrl } from '../config/api';

const BrandsTooltip = ({ open, onClose }) => {
  const [brandsData, setBrandsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      fetchBrands();
    }
  }, [open]);

  const fetchBrands = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(buildApiUrl('api/brands'), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setBrandsData(data);
    } catch (err) {
      console.error('Error fetching brands:', err);
      setError('Failed to load brands information. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '80vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1
      }}>
        <Typography variant="h6" component="div">
          Available brands
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : brandsData ? (
          <Box>
            <Typography variant="body1" paragraph sx={{ mb: 2 }}>
              A total of <strong>{brandsData.total_products.toLocaleString()}</strong> products are available across <strong>{brandsData.total_brands}</strong> brands.
            </Typography>
            
            <Typography variant="body1" paragraph sx={{ mb: 2 }}>
              The following brands are currently available:
            </Typography>
            
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 1,
              maxHeight: '400px',
              overflowY: 'auto',
              p: 2,
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              backgroundColor: '#fafafa'
            }}>
              {brandsData.brands.map((brand, index) => (
                <Chip
                  key={index}
                  label={brand}
                  size="small"
                  sx={{
                    backgroundColor: '#e3f2fd',
                    color: '#1976d2',
                    border: '1px solid #bbdefb',
                    '&:hover': {
                      backgroundColor: '#bbdefb',
                    }
                  }}
                />
              ))}
            </Box>
            
            <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
              We're constantly adding new brands and products to our database. If you don't see a product or a brand you're interested in, please let us know by emailing us at{' '}
              <a href="mailto:meow@thepickykitty.com" style={{ color: '#1976d2', textDecoration: 'none' }}>
                meow@thepickykitty.com
              </a>.
            </Typography>
          </Box>
        ) : (
          <Typography variant="body1" color="text.secondary">
            Unable to load brands information at this time.
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BrandsTooltip;
